import React, { useState, useEffect } from "react";
import { Modal, Button, Input, Space, Card, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { uploadUrl } from "../../api/api";

interface MetadataField {
  key: string;
  value: string;
}

interface URLField {
  url: string;
  metadata: MetadataField[];
}

const IngestURLModal: React.FC<{ show: boolean; handleClose: () => void }> = ({
  show,
  handleClose,
}) => {
  const initialUrlFields: URLField[] = [
    { url: "", metadata: [{ key: "title", value: "" }] },
  ];

  const [urlFields, setUrlFields] = useState<URLField[]>(initialUrlFields);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      setUrlFields(initialUrlFields);
      setError(null);
    }
  }, [show]);

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const extractTitleFromUrl = (url: string): string => {
    const urlPath = new URL(url).pathname;
    const segments = urlPath.split("/").filter((segment) => segment);

    if (segments.length === 0) {
      return "";
    }

    const lastSegment = segments[segments.length - 1].replace(/\.\w+$/, "");
    const title = lastSegment
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

    return title;
  };

  const handleUrlChange = (index: number, value: string) => {
    const updatedUrlFields = [...urlFields];
    updatedUrlFields[index].url = value;

    if (isValidUrl(value)) {
      const title = extractTitleFromUrl(value);
      const titleField = updatedUrlFields[index].metadata.find(
        (field) => field.key.toLowerCase() === "title"
      );
      if (titleField) {
        titleField.value = title;
      }
    }

    setUrlFields(updatedUrlFields);
  };

  const handleMetadataChange = (
    urlIndex: number,
    metadataIndex: number,
    field: 'key' | 'value',
    value: string
  ) => {
    const updatedUrlFields = [...urlFields];
    updatedUrlFields[urlIndex].metadata[metadataIndex][field] = value;
    setUrlFields(updatedUrlFields);
  };

  const handleAddMetadataField = (urlIndex: number) => {
    const updatedUrlFields = [...urlFields];
    updatedUrlFields[urlIndex].metadata.push({ key: "", value: "" });
    setUrlFields(updatedUrlFields);
  };

  const handleAddUrlField = () => {
    setUrlFields([
      ...urlFields,
      { url: "", metadata: [{ key: "title", value: "" }] },
    ]);
  };

  const handleSubmit = async () => {
    for (let urlField of urlFields) {
      if (urlField.url.trim() === "") {
        setError("Each URL must be filled.");
        return;
      }

      const titleField = urlField.metadata.find(
        (field) => field.key.toLowerCase() === "title"
      );
      if (!titleField || titleField.value.trim() === "") {
        setError("Each URL must have a title.");
        return;
      }
    }

    try {
      await uploadUrl(urlFields);
      message.success("URLs ingested successfully");
      setUrlFields(initialUrlFields);
      handleClose();
    } catch (error) {
      console.error("Error submitting URLs and Metadata:", error);
      message.error("Error submitting URLs");
    }
  };

  return (
    <Modal
      title="Ingest URL"
      open={show}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
      width={700}
      centered
    >
      {urlFields.map((urlField, urlIndex) => (
        <Card key={urlIndex} style={{ marginBottom: 16 }} size="small">
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Input
              placeholder="Enter URL"
              value={urlField.url}
              onChange={(e) => handleUrlChange(urlIndex, e.target.value)}
              size="large"
            />

            {urlField.metadata.map((metadataField, metadataIndex) => (
              <Space key={metadataIndex} style={{ width: '100%' }} align="baseline">
                <Input
                  placeholder="Key"
                  value={metadataField.key}
                  onChange={(e) =>
                    handleMetadataChange(urlIndex, metadataIndex, 'key', e.target.value)
                  }
                  readOnly={metadataField.key.toLowerCase() === "title"}
                  style={{ width: 150 }}
                />
                <Input
                  placeholder="Value"
                  value={metadataField.value}
                  onChange={(e) =>
                    handleMetadataChange(urlIndex, metadataIndex, 'value', e.target.value)
                  }
                  required={metadataField.key.toLowerCase() === "title"}
                  style={{ flex: 1 }}
                />
              </Space>
            ))}

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => handleAddMetadataField(urlIndex)}
              block
            >
              Add Metadata Field
            </Button>
          </Space>
        </Card>
      ))}

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={handleAddUrlField}
        block
        style={{ marginBottom: 12 }}
      >
        Add Another URL
      </Button>

      {error && <div style={{ color: 'red', marginTop: 12 }}>{error}</div>}
    </Modal>
  );
};

export default IngestURLModal;
